import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Lock } from "lucide-react";

import { PageShell } from "#/components/PageShell";
import { Button } from "#/components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "#/components/ui/card";
import { DOCUMENT_TITLES, documentHead } from "#/lib/documentTitle";
import {
  archiveMonthCalendarQueryOptions,
  archiveMonthScoresQueryOptions,
  ensureArchiveMonthQueries,
} from "#/lib/queries";
import { archiveSearchSchema } from "#/lib/routeSearchSchemas";
import { formatCalendarMonthLabel } from "#/server/game/archiveCalendarMonth";
import { loadArchiveIndexData } from "#/server/game/archiveIndex";

import { ArchiveCalendarSection } from "./-archive.calendarBoard";
import { ArchivePageIntro } from "./-archive.pageIntro";

export const Route = createFileRoute("/archive")({
  head: () => documentHead(DOCUMENT_TITLES.archive),
  component: ArchivePage,
  validateSearch: archiveSearchSchema,
  loaderDeps: ({ search }) => ({ month: search.month }),
  loader: async ({ context, deps }) => {
    const data = await loadArchiveIndexData();
    if (data.tier === "premium") {
      const monthKey =
        deps.month && data.monthKeys.includes(deps.month) ? deps.month : data.monthKeys[0];
      if (monthKey) {
        await ensureArchiveMonthQueries({
          queryClient: context.queryClient,
          monthKey,
        });
      }
    }
    return data;
  },
});

function ArchivePage() {
  const data = Route.useLoaderData();

  return (
    <PageShell>
      <section className="mx-auto w-full max-w-4xl">
        <ArchivePageIntro />
        <div className="mt-10">
          {data.tier === "free" ? (
            <div className="flex justify-center">
              <Card className="w-full max-w-sm">
                <CardHeader>
                  <CardTitle>Upgrade required</CardTitle>
                  <CardDescription>
                    Access to the archive requires a premium subscription.
                  </CardDescription>
                </CardHeader>
                <CardFooter>
                  <Link to="/premium" preload={false} className="w-full">
                    <Button size="lg" variant="premium" className="w-full">
                      <Lock className="mr-2 size-4" aria-hidden />
                      Upgrade
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </div>
          ) : (
            <ArchiveCalendarView data={data} />
          )}
        </div>
      </section>
    </PageShell>
  );
}

function ArchiveCalendarView({
  data,
}: {
  data: Extract<Awaited<ReturnType<typeof loadArchiveIndexData>>, { tier: "premium" }>;
}) {
  const { month } = Route.useSearch();
  const navigate = useNavigate({ from: "/archive" });

  const visibleMonthKey =
    month && data.monthKeys.includes(month) ? month : (data.monthKeys[0] ?? null);

  const onSelectMonth = (monthKey: string) => {
    void navigate({
      search: monthKey === data.monthKeys[0] ? {} : { month: monthKey },
    });
  };

  const calendarQuery = useQuery({
    ...archiveMonthCalendarQueryOptions(visibleMonthKey!),
    enabled: Boolean(visibleMonthKey),
  });

  const scoresQuery = useQuery({
    ...archiveMonthScoresQueryOptions(visibleMonthKey!),
    enabled: Boolean(visibleMonthKey),
  });

  return (
    <ArchiveCalendarSection
      monthKeys={data.monthKeys}
      visibleMonth={calendarQuery.data ?? null}
      visibleMonthIndex={visibleMonthKey ? data.monthKeys.indexOf(visibleMonthKey) : 0}
      onSelectMonth={onSelectMonth}
      monthScoresByMapId={scoresQuery.data}
      todayStr={data.todayStr}
      isCalendarPending={calendarQuery.isPending}
      pendingMonthLabel={visibleMonthKey ? formatCalendarMonthLabel(visibleMonthKey) : undefined}
    />
  );
}
